import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

export const calculateProgress = (current, start, target) => {
  const totalToLose = start - target;
  const lost = start - current;
  return Math.min(Math.max((lost / totalToLose) * 100, 0), 100);
};

export const getDaysRemaining = (startDate, totalDays) => {
  const start = new Date(startDate);
  const end = new Date(start);
  end.setDate(end.getDate() + totalDays);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const remaining = Math.ceil((end - today) / (1000 * 60 * 60 * 24));
  return Math.max(0, remaining);
};

export const getCurrentDay = (startDate) => {
  const start = new Date(startDate);
  start.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const dayNum = Math.floor((today - start) / (1000 * 60 * 60 * 24)) + 1;
  return Math.max(1, dayNum);
};

export const formatTime = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

export const calculateMovingAverage = (data, period = 7) => {
  if (data.length < period) return data;
  
  return data.map((item, index) => {
    if (index < period - 1) return { ...item, movingAvg: null };
    
    const slice = data.slice(index - period + 1, index + 1);
    const avg = slice.reduce((sum, d) => sum + d.weight, 0) / period;
    return { ...item, movingAvg: parseFloat(avg.toFixed(2)) };
  });
};

export const generateDateRange = (startDate, days) => {
  const dates = [];
  const start = new Date(startDate);
  
  for (let i = 0; i < days; i++) {
    const date = new Date(start);
    date.setDate(date.getDate() + i);
    dates.push(date.toISOString().split('T')[0]);
  }
  
  return dates;
};

export const downloadJSON = (data, filename) => {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const downloadCSV = (data, filename) => {
  // Flatten nested objects for CSV
  const flattenObject = (obj, prefix = '') => {
    return Object.keys(obj).reduce((acc, key) => {
      const pre = prefix.length ? `${prefix}_` : '';
      if (typeof obj[key] === 'object' && obj[key] !== null && !Array.isArray(obj[key])) {
        Object.assign(acc, flattenObject(obj[key], pre + key));
      } else {
        acc[pre + key] = obj[key];
      }
      return acc;
    }, {});
  };

  const flatData = Array.isArray(data) ? data.map(item => flattenObject(item)) : [flattenObject(data)];
  
  if (flatData.length === 0) return;
  
  const headers = Object.keys(flatData[0]);
  const csvRows = [
    headers.join(','),
    ...flatData.map(row => 
      headers.map(header => {
        const value = row[header];
        if (typeof value === 'string' && value.includes(',')) {
          return `"${value}"`;
        }
        return value ?? '';
      }).join(',')
    ),
  ];
  
  const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const compressImage = (file, maxWidth = 800) => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ratio = maxWidth / img.width;
        canvas.width = maxWidth;
        canvas.height = img.height * ratio;
        
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        
        canvas.toBlob((blob) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.readAsDataURL(blob);
        }, 'image/jpeg', 0.7);
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
};
